// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + white inner surface with subtle blue border
// - Left orange accent stripe section
// - Inputs + selects with soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - No business logic changes

import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";

const FormTimeOffRequest = () => {
  const navigate = useNavigate();

  // ✅ Theme constants (same palette)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";

  // ✅ shared sx
  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const [formData, setFormData] = useState({
    FullName: "",
    ID: "",
    Email: "",
    MobileNumber: "",
    Campus: "",
    SubCampus: "",
    Semester: "",
    LeaveStartDate: "",
    LeaveEndDate: "",
    ReasonForLeave: "",
    LeaveType: "",
  });

  const [errors, setErrors] = useState({});
  const [subCampuses, setSubCampuses] = useState([]); // (kept)
  const token = localStorage.getItem("auth");
  const [studentkey, setStudentKey] = useState("");

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const StudentKey = decodedToken.studentkey;
        setStudentKey(decodedToken.studentkey);
        const campusCode = decodedToken.campusCode;

        setFormData((prev) => ({
          ...prev,
          Campus: campusCode,
        }));

        fetchStudentDetails(campusCode, StudentKey);
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStudentDetails = async (campusCode, StudentKey) => {
    try {
      const response = await fetch(
        `${API_URL}/api/getStudentDetails?CampusCode=${encodeURIComponent(
          campusCode
        )}&StudentKey=${encodeURIComponent(StudentKey)}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      const studentDetails = Array.isArray(result) ? result[0] : result;

      setFormData((prev) => ({
        ...prev,
        ID: studentDetails.StudentID || "",
        FullName: studentDetails.FullName || "",
        Email: studentDetails.EmailAddress || "",
        MobileNumber: studentDetails.PhoneNumber || "",
        SubCampus: studentDetails.SubCampusCode || "",
        Semester: studentDetails.Semester || "",
      }));
    } catch (error) {
      console.error("Error fetching student details:", error);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.FullName.trim()) newErrors.FullName = "Name is required";
    if (!formData.ID.trim()) newErrors.ID = "ID is required";
    if (!formData.Email.trim()) newErrors.Email = "Email is required";
    if (!formData.MobileNumber.trim()) newErrors.MobileNumber = "Mobile Number is required";
    if (!formData.Campus.trim()) newErrors.Campus = "Campus is required";
    if (!formData.SubCampus.trim()) newErrors.SubCampus = "SubCampus is required";
    if (!formData.LeaveStartDate) newErrors.LeaveStartDate = "Start Date is required";
    if (!formData.LeaveEndDate) newErrors.LeaveEndDate = "End Date is required";
    if (!formData.ReasonForLeave.trim()) newErrors.ReasonForLeave = "Reason For Leave is required";
    if (!formData.LeaveType) newErrors.LeaveType = "Leave Type is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // ✅ clear field error on edit (theme UX)
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const response = await fetch(`${API_URL}/api/formTimeOffRequest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert("Time Off Request submitted successfully!");
        navigate("/ReportTimeOffRequest");
      } else {
        const errorData = await response.json();
        alert(`Failed to submit: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      alert("An error occurred while submitting the request.");
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Time Off Request
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Submit a leave request for approval
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Form surface with left orange stripe */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Request Details
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* Full Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="FullName"
                      value={formData.FullName}
                      disabled
                      error={!!errors.FullName}
                      helperText={errors.FullName}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ID"
                      name="ID"
                      value={formData.ID}
                      disabled
                      error={!!errors.ID}
                      helperText={errors.ID}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="Email"
                      value={formData.Email}
                      onChange={handleChange}
                      error={!!errors.Email}
                      helperText={errors.Email}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* Mobile */}
                  <Grid item xs={12} md={2.5}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      name="MobileNumber"
                      value={formData.MobileNumber}
                      disabled
                      error={!!errors.MobileNumber}
                      helperText={errors.MobileNumber}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* Campus */}
                  <Grid item xs={12} md={2.5}>
                    <TextField
                      fullWidth
                      label="Campus"
                      name="Campus"
                      value={formData.Campus}
                      disabled
                      error={!!errors.Campus}
                      helperText={errors.Campus}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* SubCampus */}
                  <Grid item xs={12} md={2.5}>
                    <TextField
                      fullWidth
                      label="SubCampus"
                      name="SubCampus"
                      value={formData.SubCampus}
                      disabled
                      error={!!errors.SubCampus}
                      helperText={errors.SubCampus}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* Semester (optional display, but looks nice) */}
                  <Grid item xs={12} md={1.5}>
                    <TextField
                      fullWidth
                      label="Semester"
                      name="Semester"
                      value={formData.Semester}
                      disabled
                      sx={inputSx}
                    />
                  </Grid>

                  {/* Leave Start */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Leave Start Date"
                      name="LeaveStartDate"
                      value={formData.LeaveStartDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.LeaveStartDate}
                      helperText={errors.LeaveStartDate}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* Leave End */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Leave End Date"
                      name="LeaveEndDate"
                      value={formData.LeaveEndDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.LeaveEndDate}
                      helperText={errors.LeaveEndDate}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* Leave Type */}
                  <Grid item xs={12} md={12}>
                    <FormControl fullWidth error={!!errors.LeaveType} sx={inputSx}>
                      <InputLabel>Leave Type</InputLabel>
                      <Select
                        label="Leave Type"
                        name="LeaveType"
                        value={formData.LeaveType}
                        onChange={handleChange}
                      >
                        <MenuItem value="Day Pass">Day Pass</MenuItem>
                        <MenuItem value="Sick Leave">Sick Leave</MenuItem>
                        <MenuItem value="Vacation">Vacation</MenuItem>
                        <MenuItem value="Personal">Personal</MenuItem>
                        <MenuItem value="Disciplinary">Disciplinary</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                      <FormHelperText>{errors.LeaveType}</FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Reason */}
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      label="Reason For Leave"
                      name="ReasonForLeave"
                      multiline
                      minRows={3}
                      value={formData.ReasonForLeave}
                      onChange={handleChange}
                      error={!!errors.ReasonForLeave}
                      helperText={errors.ReasonForLeave}
                      sx={inputSx}
                    />
                  </Grid>

                  {/* Actions */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                        mt: 1,
                      }}
                    >
                      <Button type="submit" variant="contained" sx={primaryBtnSx}>
                        Submit
                      </Button>
                      <Button variant="outlined" sx={outlinedBtnSx} onClick={() => navigate("/home")}>
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default FormTimeOffRequest;
